import { memo, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import {
  handleActionError,
  reportUnexpectedError,
  RETRY_HINT
} from "@/lib/errors";
import { useSellersMap } from "@/queries/sellers";
import { useProductComparisonPricing } from "@/queries/products/useProductComparisonPricing";
import { PartialSwitchError, useSwitchSupplier } from "@/queries/cart";
import {
  buildComparisonMatrix,
  collectDistinctProductIds,
  computeSupplierSubtotals,
  switchTargetFor,
  type ComparisonColumn,
  type ComparisonProductRow
} from "@/utils/supplierComparison";
import { SupplierComparisonGridSkeleton } from "./SupplierComparisonGridSkeleton";
import { SupplierComparisonGridTable } from "./SupplierComparisonGridTable";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface SupplierComparisonGridProps {
  /**
   * The carts to compare - already filtered by the sidebar's supplier and
   * category selections, same as the other two tabs receive. Rows (which
   * products appear) and each product's "current pick" follow this filtered
   * set, so unchecking a supplier or category here narrows the matrix the
   * same way it narrows "Group by Supplier"/"All Items".
   */
  carts: StoreSellerCart[];
  /**
   * Called with the number of supplier columns once the matrix has settled
   * (pricing and sellers loaded, at least one row), and with `null` while it has
   * not and on unmount. The column count is only knowable here - it spans every
   * supplier carrying the products, not just the carts - and
   * `comparison_matrix_viewed` reports it (KAN-254).
   */
  onColumnCountChange?: (count: number | null) => void;
}

/**
 * "Review Items x Suppliers" tab: an interactive products x suppliers grid.
 *
 * Rows are scoped to the filtered `carts` prop, exactly like the other two
 * tabs - so the sidebar's supplier checkboxes and category filter both apply
 * here too. Columns are the exception: for each surviving product, every
 * supplier who carries it is shown (via `getProductSupplierOffers`, inside
 * `buildComparisonMatrix`), not just the suppliers already in the filtered
 * cart - the point of this view is comparing against the real market, not
 * just replaying whatever the sidebar happens to have checked.
 *
 * Clicking a stocked, active, non-current cell switches that product to the
 * clicked supplier (`useSwitchSupplier`) - immediately, with an "Undo" action
 * on the confirmation toast. Undo is implemented as another switch back to
 * the previous supplier rather than a true rollback, so it reads the matrix
 * through a ref to get the post-refetch cart/line item rather than the stale
 * values captured when the toast was created.
 *
 * Confirmation and errors both go to the global toaster rather than inline
 * banners: anything rendered above or inside the table shifts every row below
 * it the moment it appears (and again when it leaves), which reads as the
 * page jumping around at exactly the moment the buyer is comparing prices.
 */
export const SupplierComparisonGrid = memo(function SupplierComparisonGrid({
  carts,
  onColumnCountChange
}: SupplierComparisonGridProps) {
  const productIds = useMemo(() => collectDistinctProductIds(carts), [carts]);

  const {
    products,
    isLoading: pricingLoading,
    isError: pricingIsError,
    error: pricingError
  } = useProductComparisonPricing(productIds);

  const {
    data: sellersMap,
    isLoading: sellersLoading,
    error: sellersError
  } = useSellersMap();

  const matrix = useMemo(
    () =>
      buildComparisonMatrix({
        carts,
        pricingProducts: products,
        sellersMap: sellersMap ?? {}
      }),
    [carts, products, sellersMap]
  );

  const { switchSupplier } = useSwitchSupplier();
  const [pendingRowKey, setPendingRowKey] = useState<string | null>(null);

  // Where the buyer just clicked, applied before the server confirms it.
  // A switch is two writes plus a cart refetch, so without this the ring
  // and subtotals sit unchanged for the whole round trip and the click
  // feels ignored.
  const [optimisticPick, setOptimisticPick] = useState<{
    rowKey: string;
    sellerId: string;
  } | null>(null);

  // Rows with the optimistic pick applied. Everything downstream reads
  // `currentSellerId` - the ring, and `computeSupplierSubtotals` - so
  // overriding it here moves both instantly, priced off the offers already
  // in the matrix rather than anything the server has to tell us.
  const displayRows = useMemo(() => {
    if (!optimisticPick) return matrix.rows;
    return matrix.rows.map((row) =>
      row.rowKey === optimisticPick.rowKey
        ? { ...row, currentSellerId: optimisticPick.sellerId }
        : row
    );
  }, [matrix.rows, optimisticPick]);

  const subtotals = useMemo(
    () => computeSupplierSubtotals(displayRows, matrix.columns),
    [displayRows, matrix.columns]
  );

  // Drop the optimistic pick only once the refetched carts agree with it,
  // rather than when the mutation resolves - clearing it any earlier flips
  // the ring back to the old supplier until the refetch lands.
  useEffect(() => {
    if (!optimisticPick) return;
    const row = matrix.rows.find((r) => r.rowKey === optimisticPick.rowKey);
    if (!row || row.currentSellerId === optimisticPick.sellerId) {
      setOptimisticPick(null);
    }
  }, [matrix.rows, optimisticPick]);

  // Undo fires long after the switch, by which point the carts have refetched
  // and the row's current cart/line item have changed. A ref keeps the toast's
  // callback reading the live matrix instead of the values it closed over.
  // Written in an effect rather than during render: a render can be discarded
  // (StrictMode, concurrent) and this is only ever read from a toast callback
  // well after commit, so there's nothing to gain from updating it earlier.
  const matrixRef = useRef(matrix);
  useEffect(() => {
    matrixRef.current = matrix;
  }, [matrix]);

  const handleSwitchSupplier = async (
    row: ComparisonProductRow,
    column: ComparisonColumn,
    // Set only by the toast's Undo. Undo is a switch back, so without carrying
    // it the reverted switch looks identical to a deliberate one in the pilot's
    // headline event.
    isUndo = false
  ) => {
    const target = switchTargetFor(row, column);
    if (!target) {
      return;
    }

    // `switchTargetFor` already resolved who the product moves away from, so
    // the toast's Undo can read it off the target rather than re-deriving it.
    const previousSellerId = target.change.fromSellerId;
    // The lines that exist before the switch, so Undo can tell the line the
    // switch created from one the target supplier already held.
    const rowKeysBefore = new Set(matrixRef.current.rows.map((r) => r.rowKey));
    setPendingRowKey(row.rowKey);
    setOptimisticPick({ rowKey: row.rowKey, sellerId: column.sellerId });

    try {
      // Reporting `supplier_switched` belongs to the mutation - it is the only
      // thing that knows the switch landed, and the target already carries what
      // the event needs.
      await switchSupplier({ ...target, isUndo });

      toast.success(`Switched to ${column.sellerName}`, {
        description: row.productTitle,
        position: "bottom-left",
        duration: 10000,
        action: {
          label: "Undo",
          altText: `Switch ${row.productTitle} back`,
          onClick: () =>
            handleUndo(row, column.sellerId, previousSellerId, rowKeysBefore)
        }
      });
    } catch (error) {
      // Snap back to whatever the carts actually say. That includes a partial
      // switch: the add landed as a new line at the target and the old line is
      // still at the source, so the refetch describes two lines. Keeping the
      // pick would leave the old line ringed on the target too, and the
      // subtotals would count the target twice (KAN-260).
      setOptimisticPick(null);
      // A partial switch is a real anomaly (added at the new supplier, not
      // removed from the old one), so it is reported even though the buyer gets
      // specific copy rather than a generic one.
      const toastOptions = {
        position: "bottom-left",
        duration: 10000
      } as const;
      if (error instanceof PartialSwitchError) {
        reportUnexpectedError(error, "supplier comparison partial switch");
        toast.error(
          `Added ${row.productTitle} to ${column.sellerName} but couldn't remove it from the old supplier`,
          {
            ...toastOptions,
            description: "Please check both suppliers' carts."
          }
        );
      } else {
        toast.error(
          `Couldn't switch ${row.productTitle} to ${column.sellerName}`,
          {
            ...toastOptions,
            description: handleActionError(error, {
              boundary: "supplier comparison switch",
              fallback: RETRY_HINT
            })
          }
        );
      }
    } finally {
      setPendingRowKey(null);
    }
  };

  // A switch replaces the line (add to the new cart, remove from the old), so
  // the row's key has changed by the time Undo fires. The line to move back is
  // the one the switch created: same product and variant, now with the supplier
  // it was switched to, and not among the lines that existed beforehand. When
  // the add merged into a line that supplier already held there is no such
  // line, and Undo says so rather than move the merged quantity.
  const handleUndo = (
    switched: ComparisonProductRow,
    switchedToSellerId: string,
    previousSellerId: string,
    rowKeysBefore: ReadonlySet<string>
  ) => {
    const { rows, columns } = matrixRef.current;
    const candidates = rows.filter(
      (r) =>
        r.productId === switched.productId &&
        r.variantTitle === switched.variantTitle &&
        r.currentSellerId === switchedToSellerId
    );
    const row = candidates.find((r) => !rowKeysBefore.has(r.rowKey));
    if (!row) {
      // The add merged into a line the supplier already held, so there is no
      // line of its own to move back: moving the merged line would drag the
      // supplier's original quantity along with it.
      toast.error(`Couldn't undo the switch of ${switched.productTitle}`, {
        description:
          "It merged into an existing line. Adjust the cart instead.",
        position: "bottom-left",
        duration: 10000
      });
      return;
    }
    const column = columns.find((c) => c.sellerId === previousSellerId);
    if (column) {
      void handleSwitchSupplier(row, column, true);
    }
  };

  const isLoading = pricingLoading || sellersLoading;
  const error = sellersError ?? (pricingIsError ? pricingError : null);
  useReportLoadFailure(error, "supplier comparison grid");

  // Every branch below this point replaces the table's DOM node, and the table
  // owns the scroll container - so returning any of them mid-session scrolls
  // the buyer back to the top-left of a grid they had scrolled across. They're
  // therefore first-paint states only: once there are rows to show, they stay
  // shown through refetches, transient loading, and refetch errors alike.
  const hasContent = matrix.rows.length > 0;

  const settledColumnCount =
    hasContent && !isLoading ? matrix.columns.length : null;
  useEffect(() => {
    onColumnCountChange?.(settledColumnCount);
  }, [onColumnCountChange, settledColumnCount]);
  // Leaving the tab unmounts the grid; the next opening must start unsettled.
  useEffect(() => () => onColumnCountChange?.(null), [onColumnCountChange]);

  if (!hasContent) {
    if (isLoading) {
      return <SupplierComparisonGridSkeleton />;
    }

    if (error) {
      return (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          We couldn&apos;t load the supplier comparison. Please try again.
        </div>
      );
    }

    return (
      <div
        className="flex min-h-[160px] items-center justify-center rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500"
        role="status"
      >
        Nothing to compare yet.
      </div>
    );
  }

  return (
    <SupplierComparisonGridTable
      rows={displayRows}
      columns={matrix.columns}
      subtotals={subtotals}
      onSwitchSupplier={handleSwitchSupplier}
      pendingRowKey={pendingRowKey}
    />
  );
});
